<script>
import appConfig from '@/app.config'
import PageHeader from '@/components/page-header'
import Layout from '@/router/layouts/main'
import moment from 'moment'
import Multiselect from 'vue-multiselect'
import McButton from '@/components/McButton'
import McRadioButton from '@/components/McRadioButton'
import { Helper } from '@/mixin/helper'
import { mapGetters } from 'vuex'
import {
  getAudioFiles,
  createAudioAssignment,
  conflictAudioAssignments,
  audioAssignment,
  updateAudioAssignment,
  vehicleSupport,
} from '@/services/api/master/rjm'
import { GET_GEOFENCES_FILTER } from '@/services/api/master/geofence'
import { GET_ADDRESS_CATEGORIES } from '@/services/api/setting/general/address'
import ModalAddVoice from './components/ModalAddVoice'
import ModalPlayVoice from './components/ModalPlayVoice'
import ModalVehicleSupport from './components/ModalVehicleSupport'
import ModalConflict from './components/ModalConflict'
import VehicleFeatureSupportStatus from './components/VehicleFeatureSupportStatus'
moment.locale('id')
/**
 * Starter component
 */
export default {
  page: {
    title: 'Form Voice Announcer',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    Multiselect,
    McButton,
    McRadioButton,
    ModalAddVoice,
    ModalPlayVoice,
    ModalVehicleSupport,
    ModalConflict,
  },
  mixins: [Helper],

  data() {
    return {
      form: {
        audioFiles: null,
        title: '',
        triggerType: null,
        geofences: [],
        targetVehicle: null,
        selectedVehicles: [],
      },
      fullObject: {
        audioFiles: null,
        geofences: [],
        selectedVehicles: [],
      },
      conflictData: [],
      submitInput: {},
      audioFileTableFields: [
        {
          key: 'label',
          label: this.$t('rjm.nameAudio'),
        },
        {
          key: 'audioFileUrl',
          label: 'Audio',
          isSlot: true,
          name: 'audioFileUrl',
          slotComponent: 'TableAudioPlayer',
        },
      ],
      geofenceTableFields: [
        {
          key: 'fenceName',
          label: this.$t('rjm.geofenceName'),
        },
        {
          key: 'category',
          label: this.$t('rjm.geofenceCategory'),
        },
        {
          key: 'address',
          label: this.$t('rjm.geofenceAddress'),
        },
      ],
      vehicleTableFields: [
        {
          key: 'licensePlate',
          label: this.$t('global.object.licensePlate'),
        },
        {
          key: 'vehicleType.type',
          label: this.$t('global.field.vehicleType'),
        },
        {
          key: 'vehicle2Vgroup',
          label: this.$t('global.object.fleetGroup'),
          thClass: 'mc-table--100',
          formatter: (val) => {
            if (!val || !Array.isArray(val) || val.length === 0) return '-'
            return val
              .map((item) => item.vgroup?.name)
              .filter(Boolean)
              .join(', ')
          },
        },
        {
          key: 'featureSupported',
          label: this.$t('rjm.deviceSupport'),
          isSlot: true,
          name: 'featureSupport',
          slotComponent: VehicleFeatureSupportStatus,
        },
      ],
      supportedVehiclesData: null,
    }
  },

  async created() {
    await this.getAllSupportedVehicles()
    if (this.$route.params.id) {
      await this.getAudioAssignmentData()
    }
  },

  mounted() {
    this.$root.$on('play-audio-request', this.modalPlayVoice)
  },

  beforeDestroy() {
    this.$root.$off('play-audio-request', this.modalPlayVoice)
  },

  watch: {
    'form.targetVehicle': {
      immediate: true,
      async handler(val, oldVal) {
        if (val === 'all') {
          if (this.supportedVehiclesData && this.supportedVehiclesData.data) {
            this.selectAllSupportedVehicles()
          } else {
            await this.getAllSupportedVehicles()
            this.selectAllSupportedVehicles()
          }
        } else if (val === 'specific' && oldVal === 'all') {
          this.form.selectedVehicles = []
          this.fullObject.selectedVehicles = []
        }
      },
    },
  },

  methods: {
    async getAllVehicleSupportCandidates({ search, withUnsupportedVehicles }) {
      const show = 1000
      let page = 1
      let totalPages = 1
      const data = []

      while (page <= totalPages) {
        const res = await this.$apollo.query({
          query: vehicleSupport,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              feature: 'RJM_GEOFENCE',
              pagination: { page, show },
              search,
              withUnsupportedVehicles,
            },
          },
        })

        const vehicleSupportData =
          res.data.vehiclesByFeatureSupport.vehiclesByFeatureSupport
        const pageData = vehicleSupportData?.data || []
        data.push(...pageData)

        const total = vehicleSupportData?.pagination?.total || 0
        const responseShow = vehicleSupportData?.pagination?.show || show
        totalPages = Math.ceil(total / responseShow)
        if (pageData.length === 0) break
        page += 1
      }

      return data
    },
    async getAllSupportedVehicles() {
      try {
        const filter = {
          feature: 'RJM_GEOFENCE',
          pagination: {
            page: 1,
            show: 10,
          },
          withUnsupportedVehicles: true,
        }

        const res = await this.$apollo.query({
          query: vehicleSupport,
          fetchPolicy: 'no-cache',
          variables: {
            filter: filter,
          },
        })

        const vSupport = res.data.vehiclesByFeatureSupport.vehiclesByFeatureSupport
        if (vSupport && vSupport.data) {
          this.supportedVehiclesData = vSupport
        }
      } catch (error) {
        this.$mcToast.error(error.message)
      }
    },
    selectAllSupportedVehicles() {
      const supportedVehicles = (this.supportedVehiclesData?.data || []).filter(
        (vehicle) => vehicle.featureSupported
      )
      const supportedVehicleIds =
        this.supportedVehiclesData?.allSupportedVehicleIds ||
        supportedVehicles.map((vehicle) => vehicle.id)

      this.form.selectedVehicles = [...supportedVehicleIds]
      this.fullObject.selectedVehicles = supportedVehicles.map((vehicle) => ({ ...vehicle }))
    },
    async getAudioAssignmentData() {
      try {
        const res = await this.$apollo.query({
          query: audioAssignment,
          fetchPolicy: 'no-cache',
          variables: {
            filter: {
              audioAssignmentId: parseInt(this.$route.params.id),
            },
          },
        })

        const data = res.data.audioAssignment.audioAssignment
        if (res.data.audioAssignment.isSuccessful && data) {
          this.form.title = data.label
          this.form.audioFiles = data.audioFileId
          this.fullObject.audioFiles = data.audioFile
          this.form.triggerType = data.assignmentValue

          this.form.geofences = data.geofences.map((g) => g.geofenceId)
          this.fullObject.geofences = data.geofences.map((g) => ({
            ...g,
            id: g.geofenceId,
          }))

          const selectedVehicleIds = data.vehicles.map((vehicle) => vehicle.id)
          const allSupportedVehicleIds =
            this.supportedVehiclesData?.allSupportedVehicleIds || []
          const isAllSupportedVehiclesSelected =
            allSupportedVehicleIds.length > 0 &&
            selectedVehicleIds.length === allSupportedVehicleIds.length &&
            allSupportedVehicleIds.every((id) =>
              selectedVehicleIds.some((selectedId) => String(selectedId) === String(id))
            )

          if (isAllSupportedVehiclesSelected) {
            this.form.targetVehicle = 'all'
          } else {
            this.form.selectedVehicles = selectedVehicleIds
            this.fullObject.selectedVehicles = data.vehicles.map((v) => ({ ...v }))
            this.form.targetVehicle = 'specific'
          }
        } else {
          this.$mcToast.error(res.data.audioAssignment.message)
        }
      } catch (error) {
        this.$mcToast.error(error.message)
      }
    },
    async handleOpenModal() {
      const res = await this.$apollo.query({
        query: getAudioFiles,
        fetchPolicy: 'no-cache',
        variables: {
          filter: {
            pagination: { page: 1, show: 10 },
          },
        },
      })

      const audioFilesResponse = res.data.audioFiles
      const data = audioFilesResponse.audioFiles.data || []
      const userAudioCount = data.filter((item) => item.companyId != 1).length
      const isLimitReached = audioFilesResponse.message === 'Company already have 10 audio files'

      if (userAudioCount >= 10 || isLimitReached) {
        this.$bvModal.show('modal-audio-full')
      } else {
        this.$bvModal.show('modal-add-voice')
      }
    },
    modalPlayVoice(item) {
      this.$refs.modalPlayVoice.show(item)
    },
    handleSuccess() {
      setTimeout(() => {
        this.$root.$emit('refresh-value-help', 'audioFiles')
      }, 500)
    },
    async handleSubmit() {
      try {
        const isUpdate = !!this.$route.params.id
        const input = {
          assignmentType: 'RJM_GEOFENCE',
          audioFileId: parseInt(this.form.audioFiles),
          label: this.form.title,
          geofenceIds: this.form.geofences.map((id) => parseInt(id)),
          assignmentValue: this.form.triggerType || '',
        }

        if (this.form.targetVehicle === 'specific' || this.form.targetVehicle === 'all') {
          input.vehicleIds = this.form.selectedVehicles.map((id) => parseInt(id))
        } else {
          input.vehicleIds = []
        }

        if (isUpdate) {
          input.audioAssignmentId = parseInt(this.$route.params.id)
        }

        const conflictFilter = {
          assignmentType: input.assignmentType,
          assignmentValue: input.assignmentValue,
          geofenceIds: input.geofenceIds,
          vehicleIds: input.vehicleIds,
        }

        if (isUpdate) {
          conflictFilter.ignoreAudioAssignmentId = input.audioAssignmentId
        }

        const conflictRes = await this.$apollo.query({
          query: conflictAudioAssignments,
          fetchPolicy: 'no-cache',
          variables: {
            filter: conflictFilter,
          },
        })

        if (
          conflictRes.data.audioAssignmentConflicts.isSuccessful &&
          conflictRes.data.audioAssignmentConflicts.audioAssignmentConflicts.data.length > 0
        ) {
          this.conflictData =
            conflictRes.data.audioAssignmentConflicts.audioAssignmentConflicts.data
          const clonedInput = { ...input }
          if (isUpdate) {
            delete clonedInput.assignmentType
          }
          this.submitInput = clonedInput
          this.$bvModal.show('modal-conflict')
          return
        }

        if (isUpdate) {
          delete input.assignmentType
        }

        const mutation = isUpdate ? updateAudioAssignment : createAudioAssignment
        const res = await this.$apollo.mutate({
          mutation: mutation,
          variables: {
            input: input,
          },
        })

        const result = isUpdate ? res.data.updateAudioAssignment : res.data.createAudioAssignment

        if (result.isSuccessful) {
          this.$mcToast.success(result.message)
          this.$router.push('/master/rjm')
        } else {
          this.$mcToast.error(result.message)
        }
      } catch (error) {
        this.$mcToast.error(error.message)
      }
    },
  },

  computed: {
    title() {
      return this.$route.params.id === undefined
        ? this.$t('rjm.addNewVoiceAnnouncer')
        : this.$t('rjm.voiceAnnouncer')
    },
    items() {
      return [
        {
          text: this.$t('menuitems.masterData.text'),
          to: '/master',
        },
        {
          text: 'Voice Announcer',
          to: '/master/rjm',
        },
        {
          text:
            this.$route.params.id === undefined
              ? 'Tambah Voice Announcer Baru'
              : this.form.title || 'Voice Announcer',
        },
      ]
    },
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),

    isFormInvalid() {
      const isAudioFileInvalid =
        !this.form.audioFiles ||
        (Array.isArray(this.form.audioFiles) && this.form.audioFiles.length === 0)
      const isTitleInvalid = !this.form.title
      const isTriggerTypeInvalid = !this.form.triggerType
      const isGeofencesInvalid = !this.form.geofences || this.form.geofences.length === 0
      const isTargetVehicleInvalid = !this.form.targetVehicle
      const isSpecificVehicleInvalid =
        this.form.targetVehicle === 'specific' &&
        (!this.form.selectedVehicles || this.form.selectedVehicles.length === 0)

      return (
        isAudioFileInvalid ||
        isTitleInvalid ||
        isTriggerTypeInvalid ||
        isGeofencesInvalid ||
        isTargetVehicleInvalid ||
        isSpecificVehicleInvalid
      )
    },

    resourcefulQuery() {
      return {
        audioFiles: async (opts) => {
          const filter = {
            pagination: {
              page: opts.page,
              show: opts.limit,
            },
            search: opts.search,
          }

          const res = await this.$apollo.query({
            query: getAudioFiles,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const data = res.data.audioFiles.audioFiles

          return {
            metadata: {
              total_count: data.pagination.total,
              page: data.pagination.page,
            },
            data: {
              paginated_result: data.data,
            },
          }
        },

        geofences: async (opts = {}) => {
          const filter = {
            pagination: {
              page: opts.page || 1,
              show: opts.limit || 10,
            },
            search: opts.search || undefined,
          }

          if (opts.filters && Array.isArray(opts.filters)) {
            const categoryFilter = opts.filters.find((f) => f.startsWith('category eq'))
            if (categoryFilter) {
              const categoryValue = categoryFilter.split("'")[1]
              if (categoryValue) {
                filter.category = [categoryValue]
              }
            }
          }

          const res = await this.$apollo.query({
            query: GET_GEOFENCES_FILTER,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const ugroups = res.data.geofencesFilter.geofencesFilterPaginated
          const ids =
            ugroups?.ids ??
            (Array.isArray(ugroups?.data) ? ugroups.data.map((x) => x.geofenceId) : [])

          const mappedData = Array.isArray(ugroups?.data)
            ? ugroups.data.map((x) => ({ ...x, id: x.geofenceId }))
            : []

          const result = {
            metadata: {
              total_count: ugroups.pagination.total,
              page: ugroups.pagination.page,
            },
            data: {
              paginated_result: mappedData,
              ids: ids,
            },
          }
          return result
        },

        addressCategories: async (opts) => {
          const res = await this.$apollo.query({
            query: GET_ADDRESS_CATEGORIES,
            fetchPolicy: 'no-cache',
            variables: {
              filter: {},
            },
          })

          const data = res.data.addressCategories.addressCategories

          const mappedData = data.map((item) => ({
            id: item.text,
            label: item.text,
          }))

          mappedData.unshift({ id: '', label: this.$t('global.field.allAddressCategory') })

          return {
            metadata: {
              total_count: mappedData.length,
              page: 1,
            },
            data: {
              paginated_result: mappedData,
            },
          }
        },

        vehicles: async (opts = {}) => {
          const supportFilter = Array.isArray(opts.filters)
            ? opts.filters.find((filter) => filter.startsWith('deviceSupportMode eq'))
            : null
          const supportMode = supportFilter?.split("'")[1] || ''
          const hasSpecificSupportMode = ['online', 'offline', 'unsupported'].includes(
            supportMode
          )
          const additionalFilters = Array.isArray(opts.filters)
            ? opts.filters.filter((filter) => filter !== supportFilter)
            : []

          if (hasSpecificSupportMode) {
            const candidates = await this.getAllVehicleSupportCandidates({
              search: opts.search || undefined,
              withUnsupportedVehicles: supportMode === 'unsupported',
            })
            const filteredVehicles = candidates.filter((vehicle) => {
              const matchesSupportMode =
                supportMode === 'unsupported'
                  ? !vehicle.featureSupported
                  : vehicle.featureSupported &&
                    String(vehicle.featureNetworkMode).toLowerCase() === supportMode
              const matchesAdditionalFilters = additionalFilters.every((filter) => {
                const match = filter.match(/^(.+?)\s+eq\s+'(.*)'$/)
                if (!match) return true

                return String(vehicle[match[1]] ?? '') === match[2]
              })

              return matchesSupportMode && matchesAdditionalFilters
            })
            const page = opts.page || 1
            const show = opts.limit || 10
            const start = (page - 1) * show

            return {
              metadata: {
                total_count: filteredVehicles.length,
                page,
              },
              data: {
                paginated_result: filteredVehicles.slice(start, start + show),
                ids: filteredVehicles.map((vehicle) => vehicle.id),
              },
            }
          }

          const filter = {
            feature: 'RJM_GEOFENCE',
            pagination: {
              page: opts.page || 1,
              show: opts.limit || 10,
            },
            search: opts.search || undefined,
            withUnsupportedVehicles: true,
          }

          const res = await this.$apollo.query({
            query: vehicleSupport,
            fetchPolicy: 'no-cache',
            variables: {
              filter: filter,
            },
          })

          const vSupport = res.data.vehiclesByFeatureSupport.vehiclesByFeatureSupport
          const ids = vSupport?.allSupportedVehicleIds ?? []

          const result = {
            metadata: {
              total_count: vSupport.pagination.total,
              page: vSupport.pagination.page,
            },
            data: {
              paginated_result: vSupport.data,
              ids: ids,
            },
          }
          return result
        },
      }
    },
  },
}
</script>

<template>
  <Layout>
    <div class="mc-list-header">
      <PageHeader
        :title="title"
        :items="items"
        class="mc-page-three"
        data-me="rjm-form-page-header"
      />

      <div class="clearfix mc-btn-group__list mc-btn-group__list--detail">
        <div class="float-end">
          <McButton
            variant="secondary"
            class="mx-2"
            icon="icon-fill_close"
            :text="$t('global.cancel')"
            @click="$router.push('/master/rjm')"
            data-me="rjm-form-button-cancel"
          />

          <McButton
            variant="primary"
            type="button"
            icon="icon-outline_save"
            :text="$t('global.save')"
            :disabled="isFormInvalid"
            @click="handleSubmit"
            v-if="
              hasAccessAll([
                'd9a-default-voice-announcer:create',
                'd9a-default-voice-announcer:update',
              ])
            "
            data-me="rjm-form-button-save"
          />
        </div>
      </div>
    </div>
    <FormulateForm>
      <b-card>
        <b-row>
          <b-col md="3" sm="12">
            <h4>File Audio</h4>
            <FormulateInput
              ref="audioInput"
              v-model="form.audioFiles"
              type="value-help-v2"
              name="audioFiles"
              :placeholder="`${$t('global.form.select')} ${$t('rjm.nameAudio')}`"
              class="mc-change-log-filter-input"
              label="File Audio"
              trackBy="audioFileId"
              :displayedBy="'label'"
              :listGetter="resourcefulQuery.audioFiles"
              :tableFields="audioFileTableFields"
              :fullObject="fullObject.audioFiles"
              :multiple="false"
              :hasAddButton="true"
              help="required"
              validation="required"
              @update:fullObject="($event) => (fullObject.audioFiles = $event)"
              hasSearch="false"
              @addNewData="handleOpenModal"
              hasAddAction="true"
              data-me="rjm-form-select-audio-file"
            />
          </b-col>
        </b-row>
      </b-card>

      <b-card>
        <h4>{{ $t('setting.homePage.pengaturan') }}</h4>
        <b-row>
          <b-col md="3" sm="12">
            <FormulateInput
              v-model="form.title"
              name="title"
              :label="$t('rjm.nameAlert')"
              :placeholder="$t('rjm.nameAlert')"
              help="required"
              validation="required"
              data-me="rjm-form-input-name"
            />
          </b-col>

          <!-- Column 2: Trigger Type & Geofence Selection -->
          <b-col md="5" sm="12">
            <label>{{ $t('rjm.triggerType') }}</label>
            <b-row>
              <b-col md="4" sm="12">
                <div class="d-flex flex-column gap-2 mb-3">
                  <McRadioButton
                    v-model="form.triggerType"
                    name="triggerType"
                    initialValue="enter"
                    :label="$t('rjm.enterGeofence')"
                    data-me="rjm-form-radio-trigger-type-enter"
                  />
                  <McRadioButton
                    v-model="form.triggerType"
                    name="triggerType"
                    initialValue="exit"
                    :label="$t('rjm.exitGeofence')"
                    data-me="rjm-form-radio-trigger-type-exit"
                  />
                </div>
              </b-col>
              <b-col md="6" sm="12">
                <FormulateInput
                  v-model="form.geofences"
                  type="value-help-v2"
                  name="geofences"
                  :placeholder="$t('rjm.selectGeofence')"
                  trackBy="id"
                  displayedBy="fenceName"
                  :listGetter="resourcefulQuery.geofences"
                  :tableFields="geofenceTableFields"
                  :fullObject="fullObject.geofences"
                  :multiple="true"
                  :disabled="!form.triggerType"
                  :headerFilterComponent="[
                    {
                      name: 'select-resourceful-v2_category',
                      type: 'select-resourceful-v2',
                      placeholder: $t('rjm.geofenceCategory'),
                      trackBy: 'id',
                      optionsGetter: resourcefulQuery.addressCategories,
                      optionLabelFn: ({ item }) => item.label,
                    },
                  ]"
                  help="required"
                  validation="required"
                  :canSelectAll="true"
                  :modalTitle="$t('rjm.selectGeofence')"
                  @update:fullObject="($event) => (fullObject.geofences = $event)"
                  data-me="rjm-form-select-geofence"
                />
              </b-col>
            </b-row>
          </b-col>

          <!-- Column 3: Vehicle Target Selection -->
          <b-col md="4" sm="12">
            <label>{{ $t('rjm.targetVehicle') }}</label>
            <b-row>
              <b-col md="6" sm="12">
                <div class="d-flex flex-column gap-2 mb-3">
                  <McRadioButton
                    v-model="form.targetVehicle"
                    name="targetVehicle"
                    initialValue="all"
                    :label="$t('rjm.allSupportedVehicles')"
                    data-me="rjm-form-radio-target-vehicle-all"
                  />
                  <McRadioButton
                    v-model="form.targetVehicle"
                    name="targetVehicle"
                    initialValue="specific"
                    :label="$t('rjm.specificVehicles')"
                    data-me="rjm-form-radio-target-vehicle-specific"
                  />
                </div>
              </b-col>

              <b-col md="6" sm="12">
                <span
                  class="blue-500"
                  v-if="form.targetVehicle === 'all'"
                  @click="$bvModal.show('modal-vehicle-support')"
                  data-me="rjm-form-link-view-supported-vehicles"
                  >{{ $t('rjm.viewSupportedVehicles') }}</span
                >
                <FormulateInput
                  v-if="form.targetVehicle === 'specific'"
                  v-model="form.selectedVehicles"
                  type="value-help-v2"
                  name="selectedVehicles"
                  :placeholder="$t('rjm.selectVehicles')"
                  trackBy="id"
                  displayedBy="licensePlate"
                  :listGetter="resourcefulQuery.vehicles"
                  :tableFields="vehicleTableFields"
                  :fullObject="fullObject.selectedVehicles"
                  :multiple="true"
                  :visibleTagCount="1"
                  help="required"
                  validation="required"
                  :modalTitle="$t('rjm.selectVehicles')"
                  class="mt-4"
                  :canSelectAll="true"
                  :disableRowWhen="(item) => !item.featureSupported"
                  :headerFilterComponent="[
                    {
                      name: 'deviceSupportMode',
                      type: 'select-resourceful-v2',
                      placeholder: $t('rjm.deviceSupport'),
                      trackBy: 'value',
                      optionLabel: 'label',
                      optionLabelFn: ({ item }) => item.label,
                      optionsGetter: () => ({
                        data: {
                          paginated_result: [
                            { value: '', label: $t('rjm.allVehicles') },
                            {
                              value: 'online',
                              label: $t('rjm.deviceSupportInternetRequired'),
                            },
                            {
                              value: 'offline',
                              label: $t('rjm.deviceSupportNoInternet'),
                            },
                            {
                              value: 'unsupported',
                              label: $t('rjm.deviceSupportNo'),
                            },
                          ],
                        },
                        metadata: { total_count: 4, page: 1 },
                      }),
                    },
                  ]"
                  @update:fullObject="($event) => (fullObject.selectedVehicles = $event)"
                  data-me="rjm-form-select-specific-vehicles"
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>
      </b-card>
    </FormulateForm>
    <ModalAddVoice @success="handleSuccess" />
    <ModalPlayVoice ref="modalPlayVoice" />
    <ModalConflict :items="conflictData" :input="submitInput" :triggerType="form.triggerType" />
    <ModalVehicleSupport :items="supportedVehiclesData" />
  </Layout>
</template>
